import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  descreverExclusao,
  type PreviaDaExclusao,
  type ResultadoDaExclusao,
} from '@reducee/contracts';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';

/**
 * A confirmação de excluir uma Oportunidade ou um Lead (NEX-223).
 *
 * Excluir é apagar de verdade, sem como desfazer. Por isso a janela pergunta à
 * API o que sai junto — o Lead, o Contato, os arquivos — e mostra antes do
 * clique. O que virou Contrato vem com o motivo, e sem o botão.
 *
 * `rota` é o recurso, como `/oportunidades/<id>` ou `/leads/<id>`.
 */
export function ConfirmarExclusao({
  rota,
  aoVoltar,
  aoExcluir,
}: {
  rota: string;
  aoVoltar: () => void;
  aoExcluir: () => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();

  // Sem cache: a prévia é do momento do clique, e uma velha diria menos do que sai.
  const previa = useQuery({
    queryKey: ['exclusao', rota],
    queryFn: () => chamarApi<PreviaDaExclusao>(`${rota}/exclusao`),
    staleTime: 0,
    gcTime: 0,
  });

  const excluir = useMutation({
    mutationFn: () => chamarApi<ResultadoDaExclusao>(rota, { metodo: 'DELETE' }),
    onSuccess: async (resultado) => {
      // Venda, Lead e Contato aparecem em quase toda tela: nenhuma pode seguir
      // mostrando o que acabou de sair. Menos as consultas do próprio excluído
      // — a venda, o Contato, os anexos —, que a janela ainda aberta buscaria
      // de novo para colher 404.
      const excluidos = [rota.slice(rota.lastIndexOf('/') + 1)];
      if (resultado.contatoSaiJunto) excluidos.push(resultado.contatoId);
      await clienteDeConsultas.invalidateQueries({
        predicate: (consulta) =>
          consulta.queryKey[0] !== 'exclusao' &&
          !excluidos.some((id) => consulta.queryKey.includes(id)),
      });
      aoExcluir();
    },
  });

  const erro = previa.error ?? excluir.error;

  return (
    <section
      data-teste="confirmar-exclusao"
      aria-label="Confirmar exclusão"
      className="rounded-xl border border-erro/30 bg-erro/5 p-4 text-sm"
    >
      {previa.isPending ? <p className="text-texto-suave">Conferindo o que sai junto…</p> : null}

      {previa.data?.impedimento ? (
        <p className="font-medium text-erro">{previa.data.impedimento}</p>
      ) : null}

      {previa.data && !previa.data.impedimento ? (
        <>
          <p className="font-semibold text-marinho">Sai de vez, sem como desfazer:</p>
          <ul className="mt-2 list-disc pl-5 text-texto">
            {descreverExclusao(previa.data).map((linha) => (
              <li key={linha}>{linha}</li>
            ))}
          </ul>
          {!previa.data.contatoSaiJunto ? (
            <p className="mt-2 text-xs text-texto-suave">
              O Contato fica, porque ainda tem outra venda, entrada ou Contrato.
            </p>
          ) : null}
        </>
      ) : null}

      {erro ? (
        <p role="alert" className="mt-2 text-erro">
          {erro instanceof ErroDaApi ? erro.message : 'Não foi possível excluir.'}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={aoVoltar}
          className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2 text-sm font-[750] transition hover:bg-marinho-suave"
        >
          Voltar
        </button>
        {previa.data && !previa.data.impedimento ? (
          <button
            type="button"
            onClick={() => excluir.mutate()}
            disabled={excluir.isPending}
            className="rounded-[10px] bg-erro px-3.25 py-2 text-sm font-[750] text-white transition hover:opacity-90 disabled:opacity-60"
          >
            Excluir de vez
          </button>
        ) : null}
      </div>
    </section>
  );
}
